import { API_BASE_URL, apiRequest, type ApiRequestOptions } from '../http.ts';
import type { APIResponse, MessageData } from './types/common.ts';
import type { User } from './types/users.ts';
import type { ManagersData } from './types/managedAccounts.ts';
import type { AdminUserDetailsData } from './types/users.ts';
import type { GatewayConfigurationData, GatewayControlData } from './types/system.ts';

/** Administrative user and gateway configuration endpoints. */
export class UserAdministrationApiService {
  private readonly baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    return apiRequest<T>(endpoint, options);
  }

  getUsers(): Promise<User[]> {
    return this.request<User[]>(`${this.baseUrl}/auth/users`);
  }

  adminCreateUser(user: Partial<User> & { password: string }): Promise<User> {
    return this.request<User>(`${this.baseUrl}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(user),
    });
  }

  adminUpdateUser(userId: number, user: Partial<User> & { password?: string }): Promise<User> {
    return this.request<User>(`${this.baseUrl}/auth/users/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(user),
    });
  }

  deleteUser(userId: number): Promise<APIResponse<MessageData>> {
    return this.request<APIResponse<MessageData>>(`${this.baseUrl}/auth/users/${userId}`, { method: 'DELETE' });
  }

  getUserDetails(userId: number): Promise<APIResponse<AdminUserDetailsData>> {
    return this.request<APIResponse<AdminUserDetailsData>>(
      `${this.baseUrl}/admin/users/${userId}/details`,
    );
  }

  adminConfigureUserGateway(
    userId: number,
    config: {
      username: string;
      password: string;
      mode: 'paper' | 'live';
      gateway_id?: string;
      public_port?: number;
      original_gateway_id?: string;
    },
  ): Promise<APIResponse<GatewayConfigurationData>> {
    return this.request<APIResponse<GatewayConfigurationData>>(`${this.baseUrl}/admin/users/${userId}/gateways`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
  }

  adminControlUserGateway(
    userId: number,
    gatewayId: string,
    action: 'start' | 'stop' | 'restart',
  ): Promise<APIResponse<GatewayControlData>> {
    const encodedGatewayId = encodeURIComponent(gatewayId);
    return this.request<APIResponse<GatewayControlData>>(
      `${this.baseUrl}/admin/users/${userId}/gateways/${encodedGatewayId}/control/${action}`,
      { method: 'POST' },
    );
  }

  adminActivateUserGateway(userId: number, gatewayId: string): Promise<APIResponse<GatewayConfigurationData>> {
    const encodedGatewayId = encodeURIComponent(gatewayId);
    return this.request<APIResponse<GatewayConfigurationData>>(
      `${this.baseUrl}/admin/users/${userId}/gateways/${encodedGatewayId}/activate`,
      { method: 'POST' },
    );
  }

  adminDeleteUserGateway(userId: number, gatewayId: string): Promise<APIResponse<GatewayConfigurationData>> {
    const encodedGatewayId = encodeURIComponent(gatewayId);
    return this.request<APIResponse<GatewayConfigurationData>>(
      `${this.baseUrl}/admin/users/${userId}/gateways/${encodedGatewayId}`,
      { method: 'DELETE' },
    );
  }

  adminGetUserManagers(userId: number): Promise<APIResponse<ManagersData>> {
    return this.request<APIResponse<ManagersData>>(
      `${this.baseUrl}/admin/users/${userId}/managers`,
    );
  }

  adminAddUserManager(userId: number, managerUserId: number): Promise<APIResponse<MessageData>> {
    return this.request<APIResponse<MessageData>>(`${this.baseUrl}/admin/users/${userId}/managers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ manager_user_id: managerUserId }),
    });
  }

  adminRemoveUserManager(userId: number, managerUserId: number): Promise<APIResponse<MessageData>> {
    return this.request<APIResponse<MessageData>>(`${this.baseUrl}/admin/users/${userId}/managers/${managerUserId}`, {
      method: 'DELETE',
    });
  }
}
